<template>
  <section id="intro" class="intro-section">
    <div class="container">
      <div class="intro-header">
        <h2 class="section-title">产品介绍</h2>
        <p class="section-subtitle">深入了解企业通讯录的强大功能</p>
      </div>

      <div class="intro-content" v-if="!loading && !error && articles.length > 0">
        <div
          v-for="(article, index) in articles"
          :key="article.id || index"
          class="intro-item fade-in"
          :style="{ animationDelay: `${index * 0.2}s` }"
        >
          <div class="article-content" v-html="article.content"></div>
        </div>
      </div>

      <div class="loading-container" v-if="loading">
        <div class="loading-spinner"></div>
        <p>正在加载内容...</p>
      </div>

      <div class="error-container" v-if="error">
        <div class="error-icon">⚠️</div>
        <p>{{ error }}</p>
        <button class="btn btn-primary" @click="fetchArticles">重新加载</button>
      </div>

      <div class="empty-container" v-if="!loading && !error && articles.length === 0">
        <p>暂无内容</p>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'IntroSection',
  data() {
    return {
      articles: [],
      loading: true,
      error: null
    }
  },
  mounted() {
    this.fetchArticles()
  },
  methods: {
    async fetchArticles() {
      this.loading = true
      this.error = null

      try {
        const params = new URLSearchParams({
          is_active: '1',
          page: '1',
          page_size: '50'
        })

        const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || '/api'
        const response = await fetch(`${apiBaseUrl}/articles/type/website_intro?${params}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`请求失败: ${response.status}`)
        }

        const data = await response.json()
        console.log('富文本数据:', data)

        if (data && data.data) {
          let resp = data.data.items || data.data.list || data.data || []
          if (Array.isArray(resp)) {
            this.articles = resp
          } else {
            throw new Error('数据格式错误')
          }
        }
      } catch (error) {
        console.error('获取富文本内容失败:', error)
        this.error = error.message || '加载失败，请稍后重试'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.intro-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #0f1419 0%, #1a1f2e 50%, #0f1419 100%);
  position: relative;
  overflow: hidden;
}

.intro-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 80%, rgba(0, 212, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(0, 212, 255, 0.1) 0%, transparent 50%);
  pointer-events: none;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
}

.intro-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(45deg, #00d4ff, #ffffff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 16px;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #8892b0;
  max-width: 600px;
  margin: 0 auto;
}

.intro-content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.intro-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 32px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateY(30px);
}

.intro-item.fade-in {
  animation: fadeInUp 0.6s ease forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.intro-item:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(0, 212, 255, 0.3);
  transform: translateY(-4px);
  box-shadow: 0 8px 32px rgba(0, 212, 255, 0.1);
}

.article-content {
  color: #ffffff;
  line-height: 1.8;
  font-size: 1rem;
}

.article-content :deep(h1),
.article-content :deep(h2),
.article-content :deep(h3),
.article-content :deep(h4),
.article-content :deep(h5),
.article-content :deep(h6) {
  color: #00d4ff;
  margin-bottom: 16px;
  font-weight: 600;
}

.article-content :deep(h1) { font-size: 1.8rem; }
.article-content :deep(h2) { font-size: 1.6rem; }
.article-content :deep(h3) { font-size: 1.4rem; }
.article-content :deep(h4) { font-size: 1.2rem; }
.article-content :deep(h5) { font-size: 1.1rem; }
.article-content :deep(h6) { font-size: 1rem; }

.article-content :deep(p) {
  margin-bottom: 16px;
  color: #a0aec0;
}

.article-content :deep(ul),
.article-content :deep(ol) {
  margin-bottom: 16px;
  padding-left: 24px;
}

.article-content :deep(li) {
  margin-bottom: 8px;
  color: #a0aec0;
}

.article-content :deep(strong),
.article-content :deep(b) {
  color: #ffffff;
  font-weight: 600;
}

.article-content :deep(a) {
  color: #00d4ff;
  text-decoration: none;
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);
  transition: all 0.3s ease;
}

.article-content :deep(a:hover) {
  color: #ffffff;
  border-bottom-color: #00d4ff;
}

.article-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 20px 0;
}

.loading-container,
.error-container,
.empty-container {
  text-align: center;
  padding: 60px 0;
  color: #8892b0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 212, 255, 0.3);
  border-radius: 50%;
  border-top-color: #00d4ff;
  animation: spin 1s ease-in-out infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-container {
  color: #ff6b6b;
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 20px;
}

.error-container p {
  margin-bottom: 20px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background: linear-gradient(45deg, #00d4ff, #0099cc);
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 212, 255, 0.4);
}

@media (max-width: 768px) {
  .intro-section {
    padding: 60px 0;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .section-subtitle {
    font-size: 1rem;
  }
  
  .intro-item {
    padding: 24px;
  }
  
  .article-content :deep(h1) { font-size: 1.5rem; }
  .article-content :deep(h2) { font-size: 1.4rem; }
  .article-content :deep(h3) { font-size: 1.3rem; }
  .article-content :deep(h4) { font-size: 1.2rem; }
  .article-content :deep(h5) { font-size: 1.1rem; }
  .article-content :deep(h6) { font-size: 1rem; }
  
  .article-content :deep(table) {
    font-size: 0.9rem;
  }
  
  .article-content :deep(th),
  .article-content :deep(td) {
    padding: 8px 12px;
  }
}
</style>
